import type {
  FlexProps,
  ReportTableCell,
  ReportTableProps,
} from '@/components';
import type { MinistriesModel } from '@/features/master-table/models';
import type { LedgerProjectModel } from '@/features/mold/models/ledger';
import { ProjectRelationTypeLabelModifier } from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import { BigCalculator, FALLBACK_TEXT, formatNumber } from '@/utils';
import type { CSSProperties, ReactElement } from 'react';
import type { z } from 'zod';

export const OFFSET_GRAPH_HEADING_HEIGHT = '115px';
export const LEDGER_BORDER = '1px solid black';
export const LEDGER_MIDDLE_CAPTION_WIDTH = 140;
export const LEDGER_PADDING = '2px 8px';
export const LEDGER_BG_COLOR = 'rgba(0, 0, 0, 0.25)';
export const LEDGER_RIGHT_ALIGNED_STYLE: CSSProperties = {
  justifyContent: 'flex-end',
};
export const LEDGER_FONT_SIZE = 12;

type CellStylingParameters = {
  ratio?: number;
  cellStyle?: CSSProperties;
  vertical?: boolean;
};

type TextCellType = {
  text?: string | number | null;
  type?: 'text';
  isSpace?: boolean;
  textWrapperStyle?: FlexProps['style'];
} & CellStylingParameters;

type MultipleCellType = {
  rows: Array<{ row: Array<CellType>; vertical?: boolean }>;
  type: 'multiple';
} & CellStylingParameters;

type ElementCellType = {
  type: 'element';
  element: ReactElement;
  elementWrapperStyle?: FlexProps['style'];
} & CellStylingParameters;

export type CellType = TextCellType | MultipleCellType | ElementCellType;

export type TableRowType = Array<{
  row: Array<CellType>;
  isHeader?: boolean;
  vertical?: boolean;
}>;

export const getEmptyTableDataByHeader = (
  header: TableRowType[number],
): TableRowType => [
  header,
  { row: header.row.map((v) => ({ ...v, text: FALLBACK_TEXT })) },
];

export const getEmptySectionTableDataByHeader = (
  header: ReportTableProps['header'],
): ReportTableProps['data'] => [
  { row: header.map((v) => ({ ...v, value: FALLBACK_TEXT })) },
];

/// ////////////////////////////////////////////
/// Basic Section
/// ////////////////////////////////////////////

export const getMinistryName = ({
  targetId,
  ministries,
}: {
  targetId: string | undefined;
  ministries: z.infer<typeof MinistriesModel> | undefined;
}) => {
  return ministries?.find((ministry) => ministry.id === targetId)?.name;
};

export const getRelatedProjectNames = ({
  project,
}: {
  project: LedgerProjectModel;
}) => {
  return project?.relatedProjects
    .map(
      (relatedProject) =>
        `${ProjectRelationTypeLabelModifier[relatedProject.relationType]}: ${
          relatedProject.name
        }`,
    )
    .join('\n');
};

export const getLegalBaseTableData = ({
  project,
}: {
  project: LedgerProjectModel;
}): TableRowType => {
  const header: TableRowType[number] = {
    isHeader: true,
    row: [
      { text: t('law-name'), ratio: 2 },
      { text: t('law-number'), ratio: 0.75 },
      { text: t('law-article'), ratio: 0.65 },
      { text: t('law-paragraph'), ratio: 0.2 },
      { text: t('law-paragraph-and-law-paragraph-detail'), ratio: 0.4 },
    ],
  };
  const data =
    project?.legalBases?.map<TableRowType[number]>((legalBase) => {
      return {
        row: [
          { text: legalBase.name },
          { text: legalBase.number },
          { text: legalBase.article },
          { text: legalBase.paragraph },
          { text: legalBase.paragraphSubIndex },
        ].map((v, idx) => ({ ...v, ratio: header.row[idx]?.ratio })),
      };
    }) ?? [];
  return data.length ? [header, ...data] : getEmptyTableDataByHeader(header);
};

// For Pdf common component (SectionItemTable)
export const getLegalBaseSectionTableData = ({
  project,
}: {
  project: LedgerProjectModel;
}): {
  header: ReportTableProps['header'];
  rowData: ReportTableProps['data'];
} => {
  const header: ReportTableProps['header'] = [
    { type: 'text', value: t('law-name'), ratio: 2 },
    { type: 'text', value: t('law-number'), ratio: 0.75 },
    { type: 'text', value: t('law-article'), ratio: 0.65 },
    { type: 'text', value: t('law-paragraph'), ratio: 0.2 },
    {
      type: 'text',
      value: t('law-paragraph-and-law-paragraph-detail'),
      ratio: 0.4,
    },
  ];

  const data: ReportTableProps['data'] =
    project?.legalBases?.map((legalBase) => {
      return {
        row: [
          { type: 'text', value: legalBase.name },
          {
            type: 'text',
            value: legalBase.number,
          },
          {
            type: 'text',
            value: legalBase.article,
          },
          {
            type: 'text',
            value: legalBase.paragraph,
          },
          {
            type: 'text',
            value: legalBase.paragraphSubIndex,
          },
        ] as ReportTableCell[],
      };
    }) ?? [];

  return {
    header,
    rowData: data.length ? data : getEmptySectionTableDataByHeader(header),
  };
};

export const getPlanTableData = ({
  project,
}: {
  project: LedgerProjectModel;
}): TableRowType => {
  const header: TableRowType[number] = {
    isHeader: true,
    row: [{ text: t('plan-content') }, { text: t('plan-url') }],
  };
  const data =
    project?.plans?.map<TableRowType[number]>((plan) => {
      return {
        row: [{ text: plan.contents }, { text: plan.url }],
      };
    }) ?? [];

  return data.length ? [header, ...data] : getEmptyTableDataByHeader(header);
};

// For Pdf common component (SectionItemTable)
export const getPlanSectionTableData = ({
  project,
}: {
  project: LedgerProjectModel;
}): {
  header: ReportTableProps['header'];
  rowData: ReportTableProps['data'];
} => {
  const header: ReportTableProps['header'] = [
    { type: 'text', value: t('plan-content'), ratio: 1 },
    { type: 'text', value: t('plan-url'), ratio: 1 },
  ];

  const data: ReportTableProps['data'] =
    project?.plans?.map((plan) => {
      return {
        row: [
          { type: 'text', value: plan.contents },
          { type: 'text', value: plan.url },
        ] as ReportTableCell[],
      };
    }) ?? [];

  return {
    header,
    rowData: data.length ? data : getEmptySectionTableDataByHeader(header),
  };
};

export const formatBidRate = (bidRate: number | null | undefined) => {
  return formatNumber(bidRate, {
    maximumFractionDigits: 1,
    useNegativeSign: true,
  });
};

export const formatOwnershipRatioFraction = (
  numerator: number | null | undefined,
  denominator: number | null | undefined,
  useNegativeSign = true,
) => {
  if (typeof numerator !== 'number' || typeof denominator !== 'number') {
    return FALLBACK_TEXT;
  }

  const ratio = BigCalculator.divide(numerator, denominator);

  return formatOwnershipRatioPercentage(ratio, useNegativeSign);
};

export const formatOwnershipRatioPercentage = (
  value: number | null | undefined,
  useNegativeSign = true,
) => {
  return formatNumber(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useNegativeSign,
  });
};

export const formatOwnershipRatioPartsOfFraction = (
  value: number | null | undefined,
) => {
  return formatNumber(value, { useNegativeSign: true });
};
